import React, { createContext, useContext, ReactNode, useMemo } from "react";
import { notification } from "antd";
import type { NotificationArgsProps } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";

export interface NotifyApi {
  info: (
    message: string,
    description?: ReactNode,
    config?: NotificationArgsProps
  ) => void;
  success: (
    message: string,
    description?: ReactNode,
    config?: NotificationArgsProps
  ) => void;
  error: (
    message: string,
    description?: ReactNode,
    config?: NotificationArgsProps
  ) => void;
  warning: (
    message: string,
    description?: ReactNode,
    config?: NotificationArgsProps
  ) => void;
  open: (config: NotificationArgsProps) => void;
}

const NotificationContext = createContext<NotifyApi | null>(null);

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [api, contextHolder] = notification.useNotification();

  const value: NotifyApi = useMemo(
    () => ({
      info: (message, description, config) =>
        api.info({ message, description, ...config }),
      success: (message, description, config) =>
        api.success({
          message,
          description,
          style: {
            background: "rgba(20, 82, 61, 1)",
            borderRadius: 8,
            border: "1px solid rgba(0, 188, 125, 0.3)",
          },
          icon: <CheckCircleOutlined style={{ color: "#00BC7D" }} />,
        }),
      error: (message, description, config) =>
        api.error({
          message,
          description,
          style: {
            background: "rgba(87, 25, 27, 1)",
            borderRadius: 8,
            border: "1px solid rgba(222, 14, 24, 0.3)",
          },
          icon: <CloseCircleOutlined style={{ color: "#FF6467" }} />,
        }),
      warning: (message, description, config) =>
        api.warning({
          message,
          description,
          style: {
            background: "#e8850f",
            borderRadius: 8,
            border: "1px solid rgba(232, 133, 15, 0.3)",
          },
          ...config
        }),
      open: (config) => api.open(config),
    }),
    [api]
  );

  return (
    <NotificationContext.Provider value={value}>
      {contextHolder}
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotify = (): NotifyApi => {
  const ctx = useContext(NotificationContext);
  if (!ctx)
    throw new Error("useNotify must be used within NotificationProvider");
  return ctx;
};
